import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getBonusLogs,
    getBonusTypes,
    getBonusZones,
    getTodayBonuses,
    displayBonusLabel,
    removeBonus,
    reportBonusAction,
    submitBonus,
    type BonusLogRow
} from '../api/bonuses';
import { useAuth } from '../auth/AuthContext';
import { DailyBonusesGrid } from '../components/resourceHunter/DailyBonusesGrid';
import { ResourceBonusAddPanel } from '../components/resourceHunter/ResourceBonusAddPanel';
import { ResourceHunterTabs } from '../components/resourceHunter/ResourceHunterTabs';
import { ResourceBonusGridControls } from '../components/resourceHunter/ResourceBonusGridControls';
import {
    DEFAULT_RESOURCE_BONUS_FILTERS,
    DEFAULT_RESOURCE_BONUS_SORT,
    filterAndSortBonusRows,
    getResourceBonusFilterOptions,
    hasActiveResourceBonusFilters
} from '../components/resourceHunter/resourceHunterUtils';
import { getActiveServerSlug } from '../utils/siteContext';

function getServerTime(): string {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    }).format(new Date());
}

function getServerDateString(): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(new Date());

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    return year && month && day ? `${year}-${month}-${day}` : new Date().toISOString().slice(0, 10);
}

function formatLogTime(value: string): string {
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    }).format(new Date(value));
}

function getBonusActionSummary(log: BonusLogRow): string {
    const zone = log.zone?.name ?? 'Unknown zone';
    const bonus = displayBonusLabel(log.bonusType) || 'Unknown bonus';

    switch (log.actionType) {
        case 'ADD':
            return `Added ${bonus} to ${zone}`;
        case 'REMOVE':
            return `Removed ${bonus} from ${zone}`;
        case 'ADMIN_REMOVE':
            return `Removed ${bonus} from ${zone}`;
        case 'ADMIN_RESTORE':
            return `Restored ${bonus} to ${zone}`;
        case 'REPORT':
            return `Reported an action for ${zone}`;
        case 'IMPORT':
            return `Imported ${bonus} for ${zone}`;
        default:
            return `${log.actionType.replaceAll('_', ' ')} for ${zone}`;
    }
}

export function BonusesPage() {
    const serverSlug = getActiveServerSlug();
    const serverDisplayName = serverSlug.charAt(0).toUpperCase() + serverSlug.slice(1);
    const queryClient = useQueryClient();
    const [now, setNow] = useState(getServerTime());
    const [gridFilters, setGridFilters] = useState(DEFAULT_RESOURCE_BONUS_FILTERS);
    const [gridSort, setGridSort] = useState(DEFAULT_RESOURCE_BONUS_SORT);
    const [isLogExpanded, setIsLogExpanded] = useState(false);
    const [reportingAction, setReportingAction] = useState<BonusLogRow | null>(null);
    const [reportReason, setReportReason] = useState('');

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(getServerTime());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const { isAuthenticated } = useAuth();
    const todayDate = getServerDateString();

    const typesQuery = useQuery({
        queryKey: ['bonus-types', serverSlug],
        queryFn: () => getBonusTypes(serverSlug)
    });

    const zonesQuery = useQuery({
        queryKey: ['bonus-zones', serverSlug],
        queryFn: () => getBonusZones(serverSlug)
    });

    const todayQuery = useQuery({
        queryKey: ['today-bonuses', serverSlug],
        queryFn: () => getTodayBonuses(serverSlug)
    });

    const logsQuery = useQuery({
        queryKey: ['bonus-history-logs', serverSlug, todayDate],
        queryFn: () => getBonusLogs(serverSlug, todayDate)
    });

    const invalidateToday = () => {
        void queryClient.invalidateQueries({ queryKey: ['today-bonuses', serverSlug] });
        void queryClient.invalidateQueries({ queryKey: ['bonus-history-logs', serverSlug, todayDate] });
    };

    const submitMutation = useMutation({
        mutationFn: ({ zoneId, bonusTypeId }: { zoneId: string; bonusTypeId: string }) => submitBonus(serverSlug, zoneId, bonusTypeId),
        onSuccess: invalidateToday
    });

    const removeMutation = useMutation({
        mutationFn: (bonusId: string) => removeBonus(serverSlug, bonusId),
        onSuccess: invalidateToday
    });

    const reportActionMutation = useMutation({
        mutationFn: ({ actionId, reason }: { actionId: string; reason?: string }) => reportBonusAction(serverSlug, actionId, reason),
        onSuccess: () => {
            setReportingAction(null);
            setReportReason('');
            invalidateToday();
        }
    });

    const rows = todayQuery.data ?? [];
    const types = typesQuery.data ?? [];
    const zones = zonesQuery.data ?? [];
    const canEdit = isAuthenticated;
    const filterOptions = useMemo(() => getResourceBonusFilterOptions(rows), [rows]);
    const visibleRows = useMemo(() => filterAndSortBonusRows(rows, gridFilters, gridSort), [gridFilters, gridSort, rows]);
    const hasFilters = hasActiveResourceBonusFilters(gridFilters);
    const logs = logsQuery.data ?? [];
    const lastLog = logs.length > 0 ? logs[logs.length - 1] : null;
    const lastUpdatedLabel = lastLog ? formatLogTime(lastLog.createdAt) : 'No changes yet';

    return (
        <main className="main-content">
            <section className="page-hero">
                <div className="content-wrapper py-4">
                    <h1 className="display-6 fw-semibold mb-3">
                        Resource Hunter Bonuses
                        <span className="page-hero__server d-block">{serverDisplayName}</span>
                    </h1>
                    <p className="fs-3 mb-0">{now}</p>
                </div>
            </section>

            <ResourceHunterTabs activeTab="today" />


            <div className="content-wrapper py-4">
                <div>
                    <h2 className='h3'>What's New?</h2>
                    <ul>
                        <li>Technically, the whole application is new :)</li>
                        <li>Drag and drop columns to rearrange the bonus order.</li>
                        <li>Bonus updates are temporarily rate limited for Frostreaver's launch. It'll be fine.</li>
                        <li>Actual bonus analytics - check out the trends tab.</li>
                        <li>Better sorting and filtering.</li>
                    </ul>
                </div>
                <hr className='my-3' />
                <ResourceBonusAddPanel
                    zones={zones}
                    bonusTypes={types}
                    currentRows={rows}
                    canEdit={canEdit}
                    isSubmitting={submitMutation.isPending}
                    errorMessage={submitMutation.isError ? submitMutation.error.message : null}
                    onSubmit={(zoneId, bonusTypeId) => submitMutation.mutate({ zoneId, bonusTypeId })}
                />

                {todayQuery.isLoading || typesQuery.isLoading || zonesQuery.isLoading ? <p>Loading bonuses...</p> : null}
                {todayQuery.isError ? <p className="text-danger">{todayQuery.error.message}</p> : null}

                <section className="report-panel">

                    <ResourceBonusGridControls
                        filters={gridFilters}
                        sort={gridSort}
                        expansionOptions={filterOptions.expansions}
                        zoneTypeOptions={filterOptions.zoneTypes}
                        visibleCount={visibleRows.length}
                        totalCount={rows.length}
                        hasActiveFilters={hasFilters}
                        onFiltersChange={setGridFilters}
                        onSortChange={setGridSort}
                        onResetFilters={() => setGridFilters(DEFAULT_RESOURCE_BONUS_FILTERS)}
                    />

                    <DailyBonusesGrid
                        rows={visibleRows}
                        assignmentRows={rows}
                        zones={zones}
                        bonusTypes={types}
                        canEdit={canEdit}
                        isSubmitting={submitMutation.isPending}
                        isRemoving={removeMutation.isPending}
                        onSubmit={(zoneId, bonusTypeId) => submitMutation.mutate({ zoneId, bonusTypeId })}
                        onRemove={(bonusId) => removeMutation.mutate(bonusId)}
                    />
                </section>

                <section className="report-panel mt-4 p-3 p-md-4">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div>
                            <h2 className="h4 mb-1">Today's change log</h2>
                            <div className="small text-body-secondary">
                                {logs.length} {logs.length === 1 ? 'change' : 'changes'} today · Last updated: {lastUpdatedLabel}
                            </div>
                        </div>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            aria-expanded={isLogExpanded}
                            onClick={() => setIsLogExpanded((value) => !value)}
                        >
                            {isLogExpanded ? 'Hide changes' : `Show ${logs.length} ${logs.length === 1 ? 'change' : 'changes'}`}
                        </button>
                    </div>

                    {isLogExpanded ? (
                        <div className="mt-3">
                            {logsQuery.isLoading ? <p>Loading changes...</p> : null}
                            {logsQuery.isError ? <p className="text-danger">{logsQuery.error.message}</p> : null}
                            {!logsQuery.isLoading && logs.length === 0 ? <p className="text-body-secondary mb-0">No changes have been logged today.</p> : null}
                            <div className="d-grid gap-2">
                                {logs.map((log) => (
                                    <article className="bonus-zone-card" key={log.id}>
                                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                            <div>
                                                <div className="fw-semibold">{getBonusActionSummary(log)}</div>
                                                <div className="small text-body-secondary">
                                                    {formatLogTime(log.createdAt)} · {log.actor?.username ?? 'System'}
                                                </div>
                                            </div>
                                            {isAuthenticated && log.isReportable ? (
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    type="button"
                                                    onClick={() => {
                                                        setReportingAction(log);
                                                        setReportReason('');
                                                    }}
                                                >
                                                    Report
                                                </button>
                                            ) : null}
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </section>
            </div>

            {reportingAction ? (
                <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-dialog-centered">
                        <form
                            className="modal-content"
                            onSubmit={(event) => {
                                event.preventDefault();
                                reportActionMutation.mutate({ actionId: reportingAction.id, reason: reportReason });
                            }}
                        >
                            <div className="modal-header">
                                <h2 className="modal-title h5">Report this action</h2>
                                <button
                                    className="btn-close"
                                    type="button"
                                    aria-label="Close"
                                    onClick={() => setReportingAction(null)}
                                />
                            </div>
                            <div className="modal-body">
                                <p className="mb-3">{getBonusActionSummary(reportingAction)}</p>
                                <label className="form-label" htmlFor="bonus-action-report-reason">
                                    Why are you reporting this?
                                </label>
                                <textarea
                                    id="bonus-action-report-reason"
                                    className="form-control"
                                    rows={4}
                                    value={reportReason}
                                    onChange={(event) => setReportReason(event.target.value)}
                                    placeholder="Optional, but helpful for moderators."
                                />
                                {reportActionMutation.isError ? <p className="text-danger mt-2 mb-0">{reportActionMutation.error.message}</p> : null}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-outline-secondary" type="button" onClick={() => setReportingAction(null)}>
                                    Cancel
                                </button>
                                <button className="btn btn-danger" type="submit" disabled={reportActionMutation.isPending}>
                                    {reportActionMutation.isPending ? 'Submitting...' : 'Submit report'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </main>
    );
}
